import { z } from 'zod';

import { VENUE_SLOT_MAX_CAPACITY, VenueSlotStatusSchema } from '../venue-slot/venue-slot';

/**
 * Everything about a slot a staff member may write.
 *
 * A raw **shape** rather than a schema, so `VenueSlotUpdateSchema` can
 * `.partial()` it and the two cannot drift — the `VenueWritableShape` pattern.
 * The venue is **not** here: it comes from the route, the way a photograph's
 * does, so a body cannot re-point a slot at a different partner.
 */
export const VenueSlotWritableShape = {
  /** An ISO instant. `DateTimeInput` is the one place a wall clock exists. */
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  /**
   * How many supervised meetings may run in this window at once.
   *
   * The count is claimed **transactionally** at booking time (P25c) under the
   * slot's own row lock — a `count(*)` beside the insert is check-then-act, which
   * is the defect N27 records. This number is only the ceiling that claim reads.
   */
  capacity: z.number().int().min(1).max(VENUE_SLOT_MAX_CAPACITY),
  status: VenueSlotStatusSchema,
};

/**
 * Opening one slot at a venue.
 *
 * ## The ordering rule lives HERE and not on the update
 *
 * `endsAt` after `startsAt` is checkable only where both values are present, and
 * a patch that moves one end has nothing to compare the other with. Guessing the
 * absent half would make the rule answer differently depending on which field the
 * operator touched, so the update contract carries no cross-field check at all
 * and `VenueSlotsService` re-checks the ordering against the **stored row merged
 * with the patch** — the one place both numbers exist.
 *
 * The refusal names `endsAt` deliberately: `DynamicForm` maps an issue to the
 * field in its path, and a form-level error on a two-field rule is one the
 * operator has to guess at.
 *
 * Everything policy-shaped is deliberately absent: how far ahead a slot may
 * start and how short it may be are `meetings.*` settings resolved server-side
 * (U9), because they are the deployment's answer rather than the wire's.
 */
export const VenueSlotCreateSchema = z
  .object({ ...VenueSlotWritableShape })
  .strict()
  .refine((slot) => Date.parse(slot.endsAt) > Date.parse(slot.startsAt), {
    message: 'A slot must end after it starts.',
    path: ['endsAt'],
  });

export type VenueSlotCreateRequest = z.infer<typeof VenueSlotCreateSchema>;
